/**
 * The dashboard's recent practice-sessions list refreshes when a session ends.
 *
 * listPracticeSessions provided only the broad 'Sessions' tag, and finalizing
 * a coaching session invalidated NPC progress and the dashboard summary but
 * not that list — so a just-finished conversation stayed missing (or stayed
 * unscored) on the dashboard until the 60 s cache lapsed. No polling: the list
 * now also provides PRACTICE_SESSION_LIST_TAG, and every mutation that ends or
 * finalizes a session invalidates it, whether it succeeded or not (an End the
 * server already applied still changes the list).
 *
 * Registered with enhanceEndpoints from this module only; the shared api.ts tag
 * list is not edited.
 */

import { api } from '../api';
// The endpoints enhanced below must be injected first.
import './practiceApi';
import './coachingApi';
import './npcApi';

export const PRACTICE_SESSION_LIST_TAG = 'PracticeSessionList';

/** Reads that render the recent-sessions list. */
export const PRACTICE_SESSION_LIST_QUERIES = ['listPracticeSessions'] as const;

/** Mutations after which that list is stale. */
export const PRACTICE_SESSION_LIST_MUTATIONS = [
  'endPracticeSession',
  'finalizePracticeSession',
  'endCoachingSession',
  'finalizeCoachingSession',
  'finalizeNpcSession',
] as const;

type TagList = unknown[];
type TagsField = TagList | ((...args: unknown[]) => TagList) | undefined;
interface MutableDefinition {
  providesTags?: TagsField;
  invalidatesTags?: TagsField;
}
interface Enhanceable {
  enhanceEndpoints(options: {
    addTagTypes?: readonly string[];
    endpoints?: Record<string, (definition: MutableDefinition) => void>;
  }): unknown;
}

function withListTag(field: TagsField): TagsField {
  return (...args: unknown[]) => {
    const base = typeof field === 'function' ? field(...args) : field ?? [];
    return [...base, PRACTICE_SESSION_LIST_TAG];
  };
}

const endpoints: Record<string, (definition: MutableDefinition) => void> = {};
for (const name of PRACTICE_SESSION_LIST_QUERIES) {
  endpoints[name] = (d) => { if (d) d.providesTags = withListTag(d.providesTags); };
}
for (const name of PRACTICE_SESSION_LIST_MUTATIONS) {
  endpoints[name] = (d) => { if (d) d.invalidatesTags = withListTag(d.invalidatesTags); };
}

(api as unknown as Enhanceable).enhanceEndpoints({
  addTagTypes: [PRACTICE_SESSION_LIST_TAG],
  endpoints,
});
